// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  AuditSparseEntity,
  CachedBillingDeltaListProperty,
} from '@/audit/api/types';
import { ArrowForwardRounded } from '@/constants/hardfin-standard-icons';
import { Stack, Typography } from '@mui/material';
import { DateTime } from 'luxon';

interface BillingEntry {
  date: DateTime;
  isBilled: boolean;
  isPaid: boolean;
}

interface StatusEntry extends BillingEntry {
  isNew: boolean;
}

interface BillingEntryUpdate {
  old: BillingEntry;
  new: BillingEntry;
}

interface BillingEntriesListProps {
  entries: StatusEntry[];
}

interface BillingEntriesUpdateListProps {
  entries: BillingEntryUpdate[];
}

export function AuditBillingEntriesList(props: BillingEntriesListProps) {
  return (
    <>
      {props.entries.map((entry) => (
        <Stack
          key={entry.date.toMillis()}
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ marginLeft: 2 }}
        >
          <Typography variant="caption" component="span">
            {entry.date.toFormat('MMM yyyy')}
          </Typography>
          <Typography
            variant="caption"
            component="span"
            sx={{ textDecoration: 'underline' }}
          >
            {entry.isNew ? 'added' : 'removed'}
          </Typography>
          <Typography variant="caption" component="span">
            ({entry.isBilled ? 'billed' : 'unbilled'},{' '}
            {entry.isPaid ? 'paid' : 'unpaid'})
          </Typography>
        </Stack>
      ))}
    </>
  );
}

function AuditBillingEntriesUpdateList(props: BillingEntriesUpdateListProps) {
  return (
    <>
      {props.entries.map((entry) => {
        const dateChanged = !entry.old.date.equals(entry.new.date);
        const billedStatusChanged = entry.old.isBilled !== entry.new.isBilled;
        const paidStatusChanged = entry.old.isPaid !== entry.new.isPaid;

        return (
          <Stack
            key={`${entry.old.date.toMillis()}-${entry.new.date.toMillis()}`}
            direction="column"
            sx={{ marginLeft: 2 }}
          >
            <Typography variant="caption" component="span">
              {entry.new.date.toFormat('MMM yyyy')}{' '}
              <Typography
                variant="caption"
                component="span"
                sx={{ textDecoration: 'underline' }}
              >
                updated
              </Typography>
            </Typography>
            <Stack
              sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                marginLeft: 2,
              }}
            >
              {dateChanged && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {entry.old.date.toFormat('MMM yyyy')}
                  <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
                  {entry.new.date.toFormat('MMM yyyy')}
                </Typography>
              )}
              {billedStatusChanged && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {entry.old.isBilled ? 'billed' : 'unbilled'}
                  <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
                  {entry.new.isBilled ? 'billed' : 'unbilled'}
                </Typography>
              )}
              {paidStatusChanged && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {entry.old.isPaid ? 'paid' : 'unpaid'}
                  <ArrowForwardRounded fontSize="inherit" sx={{ marginX: 1 }} />
                  {entry.new.isPaid ? 'paid' : 'unpaid'}
                </Typography>
              )}
            </Stack>
          </Stack>
        );
      })}
    </>
  );
}

interface LineItemContext {
  quantity: number;
  unitPrice: string | null;
}

interface Props {
  property: CachedBillingDeltaListProperty;
  itemContext?: AuditSparseEntity | null;
  lineItemContext: LineItemContext;
}

export function AuditCachedBillingDeltaList(props: Props) {
  if (props.property.newValue === null) {
    return null;
  }
  const quantity = props.lineItemContext.quantity;
  const unitPrice = props.lineItemContext.unitPrice;
  const name = props.itemContext?.name ?? 'Unknown';
  const added = props.property.newValue.added;
  const updated = props.property.newValue.updated;
  const removed = props.property.newValue.removed;
  const billingEntries = [
    ...(added?.map((row) => ({
      ...row,
      isNew: true,
    })) || []),
    ...(removed?.map((row) => ({
      ...row,
      isNew: false,
    })) || []),
  ].sort((a, b) => {
    return a.date.toMillis() - b.date.toMillis();
  });

  const updatedBillingEntries =
    updated?.sort((a, b) => {
      return a.old.date.toMillis() - b.old.date.toMillis();
    }) ?? [];

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        Billing periods:
      </Typography>
      <Stack sx={{ marginLeft: 2 }}>
        <Typography component="span" variant="caption">
          {name} ({quantity}
          {unitPrice && ' × ' + unitPrice})
        </Typography>
        <AuditBillingEntriesList entries={billingEntries} />
        <AuditBillingEntriesUpdateList entries={updatedBillingEntries} />
      </Stack>
    </Stack>
  );
}
